/**
 * Minimal notification shape needed for display functions.
 * Compatible with both the full Notification type and NotificationPayload.
 */
interface DisplayableNotification {
  title: string;
  message: string;
  shortformTitle?: string;
  shortformMessage?: string;
}

/**
 * Extended notification shape that includes an ID for caching purposes.
 * Used by the legacy notification handler to cache generated shortform content.
 */
interface IdentifiableNotification extends DisplayableNotification {
  id?: string;
  notificationType?: string;
}

/** Maximum character length for shortform titles */
const MAX_TITLE_LENGTH = 80;

/** Maximum character length for shortform messages */
const MAX_MESSAGE_LENGTH = 150;

/**
 * In-memory cache for generated shortform content from legacy notifications.
 * Keyed by notification ID to avoid regenerating shortform for the same notification.
 */
const shortformCache = new Map<string, { title: string; message: string }>();

/**
 * Detect whether a notification is missing shortform fields (legacy notification).
 * A notification is considered legacy if shortformTitle or shortformMessage is
 * null, undefined, or an empty string.
 *
 * @param notification - The notification to check
 * @returns true if the notification is missing shortform fields
 *
 * Validates: Requirements 5.1
 */
export function isLegacyNotification(notification: DisplayableNotification): boolean {
  return !notification.shortformTitle || !notification.shortformMessage;
}

/**
 * Remove greeting phrases from the beginning of a message.
 * Handles common patterns like "Hello John,", "Hi Jane,", "Dear Team,", etc.
 *
 * @param message - The message text to clean
 * @returns The message without the leading greeting phrase
 *
 * Validates: Requirements 1.2
 */
export function removeGreeting(message: string): string {
  const greetingPatterns = [
    /^Hello\s+[^,]+,\s*/i,
    /^Hi\s+[^,]+,\s*/i,
    /^Dear\s+[^,]+,\s*/i,
    /^Greetings\s+[^,]+,\s*/i,
    /^Good\s+(morning|afternoon|evening)\s+[^,]+,\s*/i,
  ];

  let cleaned = message;
  for (const pattern of greetingPatterns) {
    cleaned = cleaned.replace(pattern, '');
  }

  return cleaned.trim();
}

/**
 * Remove URLs from text content.
 * Replaces http/https URLs with empty string and cleans up extra whitespace.
 *
 * @param text - The text to clean
 * @returns The text with URLs removed
 *
 * Validates: Requirements 8.3
 */
export function removeUrls(text: string): string {
  return text
    .replace(/https?:\/\/[^\s)]+/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

/**
 * Truncate text to a maximum length, appending ellipsis ("...") if truncated.
 * Preserves the most important information at the beginning of the text.
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum allowed length
 * @returns The original text if within limit, or truncated text with "..." appended
 *
 * Validates: Requirements 9.1, 9.2, 9.3, 9.4
 */
export function truncateText(text: string, maxLength: number): string {
  if (!text) {
    return '';
  }
  if (text.length <= maxLength) {
    return text;
  }
  return text.substring(0, maxLength - 3) + '...';
}

/**
 * Generate shortform content from longform title and message.
 * Applies greeting removal, URL removal, and truncation to produce
 * concise shortform versions suitable for notification list display.
 *
 * @param longformTitle - The full notification title
 * @param longformMessage - The full notification message
 * @returns An object with generated shortformTitle and shortformMessage
 *
 * Validates: Requirements 5.1, 5.2, 5.5
 */
export function generateShortformFromLongform(
  longformTitle: string,
  longformMessage: string
): { title: string; message: string } {
  // For title: truncate to 80 chars (titles typically don't have greetings)
  const shortTitle = truncateText(longformTitle, MAX_TITLE_LENGTH);

  // For message: remove greeting, remove URLs, then truncate to 150 chars
  let cleanMessage = removeGreeting(longformMessage);
  cleanMessage = removeUrls(cleanMessage);
  const shortMessage = truncateText(cleanMessage, MAX_MESSAGE_LENGTH);

  return {
    title: shortTitle,
    message: shortMessage,
  };
}

/**
 * Get display content for a notification, handling both new and legacy formats.
 * For notifications with shortform fields, returns them directly.
 * For legacy notifications (missing shortform fields), generates shortform
 * on-the-fly from longform content and caches the result.
 *
 * This is the primary function for obtaining display-ready notification content.
 *
 * @param notification - The notification to get display content for
 * @returns An object with title and message suitable for list display
 *
 * Validates: Requirements 5.1, 5.2
 */
export function getDisplayContent(notification: IdentifiableNotification): { title: string; message: string } {
  // If shortform fields are present and non-empty, use them directly
  if (notification.shortformTitle && notification.shortformMessage) {
    return {
      title: notification.shortformTitle,
      message: notification.shortformMessage,
    };
  }

  // Check cache first (if notification has an ID)
  if (notification.id) {
    const cached = shortformCache.get(notification.id);
    if (cached) {
      return cached;
    }
  }

  // Generate shortform from longform content
  const generated = generateShortformFromLongform(
    notification.title,
    notification.message
  );

  // Cache the result if notification has an ID
  if (notification.id) {
    shortformCache.set(notification.id, generated);
  }

  return generated;
}

/**
 * Clear the shortform cache. Useful for testing or when notifications are refreshed.
 */
export function clearShortformCache(): void {
  shortformCache.clear();
}

/**
 * Get the current size of the shortform cache.
 * Useful for monitoring and testing.
 */
export function getShortformCacheSize(): number {
  return shortformCache.size;
}

/**
 * Get the display title for a notification.
 * Prefers shortform title when available and non-empty,
 * falls back to longform title otherwise.
 *
 * @param notification - The notification to get the display title for
 * @returns The shortform title if available, otherwise the longform title
 */
export function getDisplayTitle(notification: DisplayableNotification): string {
  return notification.shortformTitle || notification.title;
}

/**
 * Get the display message for a notification.
 * Prefers shortform message when available and non-empty,
 * falls back to longform message otherwise.
 *
 * @param notification - The notification to get the display message for
 * @returns The shortform message if available, otherwise the longform message
 */
export function getDisplayMessage(notification: DisplayableNotification): string {
  return notification.shortformMessage || notification.message;
}

/**
 * Truncate text to a maximum length, appending ellipsis ("...") if truncated.
 * If the text is within the limit, it is returned unchanged.
 *
 * @param text - The text to truncate
 * @param maxLength - Maximum allowed length (must be >= 4 to allow at least one char + "...")
 * @returns The original text if within limit, or truncated text with "..." appended
 *
 * Validates: Requirements 11.3, 11.4
 */
export function truncateForBrowser(text: string, maxLength: number): string {
  if (text.length <= maxLength) {
    return text;
  }
  return text.slice(0, maxLength - 3) + '...';
}


/**
 * Format a date string as a relative time (e.g., "2 minutes ago").
 * Handles various time ranges:
 * - "just now" for < 1 minute
 * - "X minutes ago" for < 1 hour
 * - "X hours ago" for < 24 hours
 * - "X days ago" for < 7 days
 * - "X weeks ago" for < 30 days
 * - The actual date for older notifications
 *
 * @param dateString - ISO date string to format
 * @returns A human-readable relative time string
 *
 * Validates: Requirements 2.5
 */
export function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();

  // Handle future dates or invalid dates
  if (isNaN(diffMs) || diffMs < 0) {
    return 'just now';
  }

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);
  const diffWeeks = Math.floor(diffDays / 7);

  if (diffMinutes < 1) {
    return 'just now';
  }

  if (diffMinutes < 60) {
    return diffMinutes === 1 ? '1 minute ago' : `${diffMinutes} minutes ago`;
  }

  if (diffHours < 24) {
    return diffHours === 1 ? '1 hour ago' : `${diffHours} hours ago`;
  }

  if (diffDays < 7) {
    return diffDays === 1 ? '1 day ago' : `${diffDays} days ago`;
  }

  if (diffDays < 30) {
    return diffWeeks === 1 ? '1 week ago' : `${diffWeeks} weeks ago`;
  }

  // For older notifications, show the actual date
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}
